import { useLazyQuery, useMutation } from '@apollo/client';
import { CustomIcon } from '@assets/CustomIcon';
import { ADD_PRO_UPERCUT, GET_FROM_SIRET_PAPPERS } from '@gql/pros';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    InputBase,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// SIRET mask: 14 digits formatted as "XXX XXX XXX XXXXX"
const SIRET_MASK = [
    /\d/, /\d/, /\d/, ' ',
    /\d/, /\d/, /\d/, ' ',
    /\d/, /\d/, /\d/, ' ',
    /\d/, /\d/, /\d/, /\d/, /\d/,
];

const applySiretMask = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 14);
    let result = '';
    let digitIndex = 0;
    for (const part of SIRET_MASK) {
        if (digitIndex >= digits.length) break;
        if (typeof part === 'string') {
            if (digitIndex < digits.length) result += part;
        } else {
            result += digits[digitIndex++];
        }
    }
    return result;
};

const stripSiretMask = (masked: string): string => masked.replace(/\D/g, '');

interface AddCompanyModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const AddCompanyModal = ({ open, onClose, onSuccess }: AddCompanyModalProps) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [siretInput, setSiretInput] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const [getFromSiretPappers, { data: pappersData, loading: searchLoading, error: searchError }] =
        useLazyQuery(GET_FROM_SIRET_PAPPERS, { fetchPolicy: 'no-cache' });

    const [addProUpercut, { loading: submitLoading, error: submitError }] = useMutation(ADD_PRO_UPERCUT);

    const pappers = pappersData?.getFromSiretPappers;
    const isClaimed = pappers?.isClaimed ?? false;

    const handleSiretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSiretInput(applySiretMask(e.target.value));
    };

    const handleSearch = async () => {
        const siret = stripSiretMask(siretInput);
        if (siret.length !== 14) return;
        setHasSearched(true);
        await getFromSiretPappers({ variables: { siret } });
    };

    const handleChoose = async () => {
        const siret = stripSiretMask(siretInput);
        try {
            const { data } = await addProUpercut({
                variables: { proInput: { email: emailInput.trim(), siret } },
            });
            const proId = data?.addProUpercut?._id;
            handleClose();
            onSuccess?.();
            if (proId) {
                navigate(`/companies/${proId}/edit`);
            }
        } catch {
            // error displayed via Alert below
        }
    };

    const handleClose = () => {
        setSiretInput('');
        setEmailInput('');
        setHasSearched(false);
        onClose();
    };

    const siretDigits = stripSiretMask(siretInput);
    const isValidSiret = siretDigits.length === 14;
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim());

    const isDark = theme.palette.mode === 'dark';
    const grayBg = isDark ? theme.palette.disableFormBlack.main : '#f9f9f9';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                },
            }}
        >
            <DialogContent sx={{ p: 0, position: 'relative' }}>
                {/* Close button */}
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Stack spacing={5} sx={{ p: { xs: 3, sm: 5 } }}>
                    {/* Heading */}
                    <Stack spacing={1.5} alignItems="center" textAlign="center">
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ color: theme.palette.text.primary }}
                        >
                            Sélectionnez votre entreprise
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: theme.palette.text.secondary, maxWidth: 560 }}
                        >
                            Pour trouver votre entreprise, il vous suffit de la rechercher en entrant son n° SIRET ou son nom.
                        </Typography>
                    </Stack>

                    {/* Search area */}
                    <Stack alignItems="center" spacing={0}>
                        {/* Combined pill search bar */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                width: '100%',
                                maxWidth: 620,
                                border: `1.5px solid ${theme.palette.primary.main}`,
                                borderRadius: '24px',
                                pl: 3,
                                pr: 1,
                                py: 1,
                                backgroundColor: theme.palette.background.paper,
                                boxShadow: '0px 4px 40px rgba(112, 144, 176, 0.14)',
                            }}
                        >
                            <SearchIcon sx={{ color: theme.palette.primary.main, flexShrink: 0, fontSize: 20 }} />
                            <InputBase
                                value={siretInput}
                                onChange={handleSiretChange}
                                placeholder="XXX XXX XXX XXXXX"
                                inputProps={{ maxLength: 19 }}
                                sx={{
                                    flex: 1,
                                    fontSize: '1.125rem',
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                    '& input::placeholder': {
                                        color: theme.palette.text.disabled,
                                        opacity: 1,
                                    },
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && isValidSiret && !searchLoading) {
                                        handleSearch();
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                disabled={!isValidSiret || searchLoading}
                                startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                                sx={{
                                    borderRadius: '24px',
                                    px: 3,
                                    py: 1.25,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    minWidth: 140,
                                }}
                            >
                                Rechercher
                            </Button>
                        </Box>

                        {/* Hint */}
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
                            <HelpOutlineIcon sx={{ fontSize: 13, color: theme.palette.neutralDarkGray2.main }} />
                            <Typography variant="caption" sx={{ color: theme.palette.neutralDarkGray2.main }}>
                                Vous pouvez trouver votre SIRET sur{' '}
                                <Link
                                    href="https://pappers.fr"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: theme.palette.primary.main }}
                                >
                                    pappers.fr.
                                </Link>
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Error after search */}
                    {hasSearched && searchError && (
                        <Alert severity="error">
                            Impossible de récupérer les données SIRET. Vérifiez le numéro et réessayez.
                        </Alert>
                    )}

                    {/* Submit error */}
                    {submitError && (
                        <Alert severity="error">
                            Une erreur est survenue lors de l'ajout. Veuillez réessayer.
                        </Alert>
                    )}

                    {/* Company result card */}
                    {hasSearched && pappers && !searchError && (
                        <Box
                            sx={{
                                border: `1px solid`,
                                borderColor: isClaimed
                                    ? theme.palette.errorRed.main
                                    : theme.palette.primaryPurple30.main,
                                borderRadius: 2,
                                overflow: 'hidden',
                                backgroundColor: theme.palette.background.paper,
                            }}
                        >
                            {/* Card header */}
                            <Box
                                sx={{
                                    px: 3,
                                    py: 2.5,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        {/* Status badge */}
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                px: 1.5,
                                                py: 0.25,
                                                borderRadius: '16px',
                                                backgroundColor: isClaimed
                                                    ? theme.palette.errorRed.main
                                                    : theme.palette.green.main,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {!isClaimed && <CheckIcon sx={{ fontSize: 12, color: 'white' }} />}
                                            <Typography
                                                variant="caption"
                                                fontWeight={700}
                                                sx={{ color: 'white', lineHeight: 1.5 }}
                                            >
                                                {isClaimed ? 'Déjà enregistré' : 'Disponible'}
                                            </Typography>
                                        </Box>

                                        {/* Company name */}
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            {pappers.pro?.commercialName || pappers.pro?.name || '—'}
                                        </Typography>

                                        {/* SIRET pill */}
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: '16px',
                                                backgroundColor: grayBg,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
                                            >
                                                {pappers.pro?.siret || siretDigits}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    {/* Boss name */}
                                    {(pappers.pro?.bossFirstname || pappers.pro?.bossLastname) && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: theme.palette.neutralGray.main, mt: 0.5, display: 'block' }}
                                        >
                                            Dirigé par{' '}
                                            <strong>
                                                {[pappers.pro?.bossFirstname, pappers.pro?.bossLastname]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </strong>
                                        </Typography>
                                    )}
                                </Box>

                                {/* Choisir button */}
                                <Tooltip title={isClaimed ? 'Ce SIRET est déjà enregistré sur Upercut' : ''}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            onClick={handleChoose}
                                            disabled={isClaimed || !isValidEmail || submitLoading}
                                            endIcon={
                                                submitLoading
                                                    ? <CircularProgress size={14} color="inherit" />
                                                    : <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                            }
                                            sx={{
                                                borderRadius: '48px',
                                                px: 2,
                                                py: 0.75,
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                                fontWeight: 700,
                                            }}
                                        >
                                            Choisir
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            {/* Email input for unclaimed companies */}
                            {!isClaimed && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <TextField
                                        label="Adresse email du compte"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        placeholder="email@example.com"
                                        type="email"
                                        fullWidth
                                        size="small"
                                        required
                                        helperText="Cette adresse sera utilisée pour créer le compte"
                                    />
                                </Box>
                            )}

                            {/* Details row */}
                            <Box
                                sx={{
                                    px: 3,
                                    py: 2,
                                    backgroundColor: grayBg,
                                    display: 'flex',
                                    gap: { xs: 3, sm: 6 },
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                }}
                            >
                                {/* Activité */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 160 }}>
                                    <CustomIcon
                                        name="physics"
                                        size={14}
                                        color={theme.palette.text.secondary as string}
                                        style={{ marginTop: 2, flexShrink: 0 }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.5 }}
                                        >
                                            Activité
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            {pappers.pro?.ape || '—'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Forme Juridique */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 120 }}>
                                    <CustomIcon
                                        name="document"
                                        size={14}
                                        color={theme.palette.text.secondary as string}
                                        style={{ marginTop: 2, flexShrink: 0 }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.5 }}
                                        >
                                            Forme Juridique
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            {pappers.pro?.legal || '—'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Adresse */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: 1, minWidth: 180 }}>
                                    <CustomIcon
                                        name="map-marker"
                                        size={14}
                                        color={theme.palette.text.secondary as string}
                                        style={{ marginTop: 2, flexShrink: 0 }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.5 }}
                                        >
                                            Adresse
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            {[pappers.pro?.address, pappers.pro?.postal, pappers.pro?.city]
                                                .filter(Boolean)
                                                .join(', ') || '—'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Footer */}
                    <Stack spacing={2} alignItems="center">
                        <Stack spacing={1} alignItems="center" textAlign="center">
                            <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
                                Vous ne trouvez pas votre société ?
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: theme.palette.neutralDarkGray2.main, maxWidth: 400 }}
                            >
                                Contactez-nous pour enregistrer votre entreprise et commencer à utiliser Upercut.
                            </Typography>
                        </Stack>
                        <Button
                            variant="outlined"
                            startIcon={<HeadsetMicIcon />}
                            sx={{
                                borderRadius: '24px',
                                px: 3,
                                py: 1.25,
                                fontWeight: 700,
                                fontSize: '1rem',
                            }}
                        >
                            Contactez-nous
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default AddCompanyModal;
